import React, { useEffect, useRef, useState } from "react";
import ClubListMenu from "./ClubListMenu";
import {
  ShareBadge,
  AvatarStack,
  SecondaryButton,
  PrimaryButton,
  OverflowButton,
  MenuPopover,
  MenuItem,
  Eyebrow,
  GLYPH,
} from "./v2";
import { CLUB_SORT_OPTIONS } from "../../constants/clubRadarChoices";

// Club Radar list header (desktop, frames 1a/1b — V2 rebuild, 1:1 from the
// artboard). Title 18px/800 + share-tier pill, meta row (avatar stack ·
// "Created by <name>" · italic description + Edit), right-side controls
// "Share to chat" / "Sort: … ▾" / ⋯ (active state when the menu is open),
// and the role-aware ⋯ menu — the shared ClubListMenu (frames 1b ≡ 2d, ONE
// component; colleague and My-Radar variants via the existing `canManage`/
// `is_default` gates). Presentation only — same handlers as before.

// Nested "choose a target list" panel for Move/Copy players to… (list-level).
const ListChooser = ({ lists, excludeId, onPick }) => {
  const targets = lists.filter((l) => l.id !== excludeId);
  return (
    <div style={{ maxHeight: 220, overflowY: "auto" }}>
      {targets.length === 0 && (
        <div style={{ fontSize: 12, color: "#9CA3AF", padding: "8px 10px" }}>No other lists</div>
      )}
      {targets.map((l) => (
        <MenuItem key={l.id} onClick={() => onPick(l)}>{l.name}</MenuItem>
      ))}
    </div>
  );
};

// Share-tier pill beside the title (1a "Personal · only you" / 1b
// "Shared · N people" / "Shared with club") — real state, counts computed.
const TierPill = ({ list }) => {
  const tier = list.share_tier || "personal";
  if (tier === "personal") {
    return <ShareBadge tone="neutral">Personal · only you</ShareBadge>;
  }
  if (tier === "club") {
    return <ShareBadge tone="shared">Shared with club</ShareBadge>;
  }
  const n = list.shared_member_count ?? (list.shared_member_preview || []).length;
  return <ShareBadge tone="shared">{`Shared · ${n} ${n === 1 ? "person" : "people"}`}</ShareBadge>;
};

const ClubListHeader = ({
  list,
  count,
  allLists = [],
  canManage,
  sortBy,
  onSortChange,
  onShareToChat,
  onRename,
  onSaveDescription,
  onManageSharing,
  onBulkMove,
  onBulkCopy,
  onDelete,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [submenu, setSubmenu] = useState(null); // 'move' | 'copy'
  const [renaming, setRenaming] = useState(false);
  const [renameVal, setRenameVal] = useState(list.name || "");
  const [editingDesc, setEditingDesc] = useState(false);
  const [descVal, setDescVal] = useState(list.description || "");
  const menuRef = useRef(null);

  const isDefault = !!list.is_default;
  const tier = list.share_tier || "personal";

  useEffect(() => {
    setRenameVal(list.name || "");
    setDescVal(list.description || "");
  }, [list.id, list.name, list.description]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
        setSubmenu(null);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [menuOpen]);

  const closeMenu = () => {
    setMenuOpen(false);
    setSubmenu(null);
  };

  const submitRename = () => {
    const v = renameVal.trim();
    setRenaming(false);
    if (v && v !== list.name) onRename(list.id, v);
  };

  const submitDesc = () => {
    setEditingDesc(false);
    if (descVal !== (list.description || "")) onSaveDescription(list.id, descVal.trim());
  };

  return (
    <div style={{ position: "relative" }}>
      {/* Title row */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
        {renaming ? (
          <input
            value={renameVal}
            onChange={(e) => setRenameVal(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submitRename()}
            onBlur={submitRename}
            autoFocus
            maxLength={60}
            className="outline-none"
            style={{
              fontSize: 18,
              fontWeight: 800,
              letterSpacing: "-0.2px",
              color: "#111827",
              border: "1px solid #E5E7EB",
              borderRadius: 10,
              padding: "2px 8px",
              maxWidth: 320,
              fontFamily: "inherit",
            }}
          />
        ) : (
          <span
            style={{
              fontSize: 18,
              fontWeight: 800,
              letterSpacing: "-0.2px",
              color: "#111827",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {list.name}
          </span>
        )}
        <TierPill list={list} />

        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          {count > 0 && (
            <SecondaryButton height={34} pad="0 13px" fontSize={13} fontWeight={600} color="#111827" onClick={onShareToChat}>
              Share to chat
            </SecondaryButton>
          )}
          {/* "Sort: … ▾" — the platform's native select, styled per artboard
              (appearance none + ▾ caret overlay); same onChange handler. */}
          <span style={{ position: "relative", display: "inline-flex" }}>
            <select
              value={sortBy}
              onChange={(e) => onSortChange(e.target.value)}
              className="cursor-pointer outline-none"
              style={{
                height: 34,
                border: "1px solid #E5E7EB",
                borderRadius: 10,
                background: "#FFFFFF",
                color: "#374151",
                fontSize: 13,
                fontWeight: 600,
                padding: "0 26px 0 13px",
                appearance: "none",
                WebkitAppearance: "none",
                MozAppearance: "none",
                fontFamily: "inherit",
              }}
            >
              {CLUB_SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{`Sort: ${o.label}`}</option>
              ))}
            </select>
            <span
              aria-hidden="true"
              style={{
                position: "absolute",
                right: 11,
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: 10,
                color: "#9CA3AF",
                pointerEvents: "none",
              }}
            >
              {GLYPH.caretDown}
            </span>
          </span>
          <span ref={menuRef} style={{ position: "relative", display: "inline-flex" }}>
            <OverflowButton
              size={34}
              radius={10}
              active={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
              aria-haspopup="true"
              aria-expanded={menuOpen}
              aria-label="List actions"
              className="cursor-pointer"
            />
            {menuOpen && (
              submenu ? (
                <MenuPopover style={{ position: "absolute", right: 0, top: 42, zIndex: 40, animation: "tp-pop 0.12s ease" }}>
                  <Eyebrow fontSize={10.5} spacing=".05em" style={{ padding: "6px 10px 2px" }}>
                    {submenu === "move" ? "Move all players to" : "Copy all players to"}
                  </Eyebrow>
                  <ListChooser
                    lists={allLists}
                    excludeId={list.id}
                    onPick={(target) => {
                      closeMenu();
                      (submenu === "move" ? onBulkMove : onBulkCopy)(target);
                    }}
                  />
                </MenuPopover>
              ) : (
                <ClubListMenu
                  canManage={canManage}
                  isDefault={isDefault}
                  count={count}
                  onRename={() => { closeMenu(); setRenaming(true); }}
                  onEditDescription={() => { closeMenu(); setEditingDesc(true); }}
                  onMovePlayers={() => setSubmenu("move")}
                  onCopyPlayers={() => setSubmenu("copy")}
                  onManageSharing={() => { closeMenu(); onManageSharing(); }}
                  onShareToChat={() => { closeMenu(); onShareToChat(); }}
                  onDelete={() => { closeMenu(); onDelete(); }}
                  style={{ position: "absolute", right: 0, top: 42, zIndex: 40, animation: "tp-pop 0.12s ease" }}
                />
              )
            )}
          </span>
        </div>
      </div>

      {/* Meta row (1b): avatar stack · Created by <name> · "description" · Edit */}
      {editingDesc ? (
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <input
            value={descVal}
            onChange={(e) => setDescVal(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submitDesc()}
            autoFocus
            maxLength={160}
            placeholder="Add a description, e.g. Winter window, U21 focus"
            className="flex-1 outline-none"
            style={{ height: 34, border: "1px solid #E5E7EB", borderRadius: 10, padding: "0 11px", fontSize: 12.5, fontFamily: "inherit" }}
          />
          <PrimaryButton height={34} pad="0 14px" fontSize={12.5} onClick={submitDesc}>Save</PrimaryButton>
          <SecondaryButton height={34} pad="0 12px" fontSize={12.5} fontWeight={600} color="#6B7280" onClick={() => { setEditingDesc(false); setDescVal(list.description || ""); }}>
            Cancel
          </SecondaryButton>
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, flexWrap: "wrap", minWidth: 0 }}>
          {tier === "selected" && (list.shared_member_preview || []).length > 0 && (
            <AvatarStack
              members={(list.shared_member_preview || []).map((m) => ({
                name: m.name || m.initials,
                initials: m.initials,
                imageUrl: m.avatar_url,
              }))}
              size={22}
            />
          )}
          {list.created_by && (
            <span style={{ fontSize: 12, color: "#6B7280" }}>
              Created by{" "}
              <span style={{ fontWeight: 700, color: "#374151" }}>
                {list.created_by.is_me ? "you" : list.created_by.name}
              </span>
            </span>
          )}
          {list.description ? (
            <>
              <span style={{ fontSize: 12, color: "#374151", fontStyle: "italic", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {GLYPH.quoteOpen}{list.description}{GLYPH.quoteClose}
              </span>
              {canManage && !isDefault && (
                <button
                  type="button"
                  onClick={() => setEditingDesc(true)}
                  className="cursor-pointer"
                  style={{ fontSize: 11, color: "#2283FF", fontWeight: 700, background: "none", border: "none", padding: 0, fontFamily: "inherit" }}
                >
                  Edit
                </button>
              )}
            </>
          ) : canManage && !isDefault ? (
            <button
              type="button"
              onClick={() => setEditingDesc(true)}
              className="cursor-pointer"
              style={{ fontSize: 11, color: "#2283FF", fontWeight: 700, background: "none", border: "none", padding: 0, fontFamily: "inherit" }}
            >
              {GLYPH.plus} Add a description
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default ClubListHeader;
